import { FlexibleModuleEnum } from '../../../../generated/graphql';
import type {
    PageBuilderModuleType,
    ColumnContentNodeProps,
    ColumnContentTextNodeProps,
    ColumnContentProps,
} from '@modules/page-builder/types';

const filterTextModules = (modules: PageBuilderModuleType[]) =>
    modules.filter((module) =>
        [
            FlexibleModuleEnum.SingleParagraphModule,
            FlexibleModuleEnum.DuoParagraphModule,
            FlexibleModuleEnum.ImageParagraphModule,
            FlexibleModuleEnum.HeadingModule,
            FlexibleModuleEnum.QuoteModule,
        ].includes(module.presetType as FlexibleModuleEnum)
    );

const shouldContinue = (text: string, limit: number) =>
    limit == 0 || text.length < limit;

const isRootNode = (
    node: ColumnContentProps | ColumnContentNodeProps
): node is ColumnContentProps =>
    (node as ColumnContentProps).root !== undefined;

const isTextNode = (
    node: ColumnContentNodeProps | ColumnContentTextNodeProps
): node is ColumnContentTextNodeProps =>
    (node as ColumnContentTextNodeProps).text !== undefined;

const collectTextFromNode = (
    node: ColumnContentNodeProps,
    limit: number = 0,
    text: string = ''
) => {
    if (shouldContinue(text, limit)) {
        // if it is text node - simply return it's content
        if (isTextNode(node)) {
            return `${text} ${node.text}`;
        }

        // if not - go for it's children
        if (node.children) {
            node.children.forEach(
                (child) => (text = collectTextFromNode(child, limit, text))
            );
        }
    }

    return text;
};

export const collectTextFromModules = (
    modules: PageBuilderModuleType[],
    limit: number = 0
): string => {
    let text = '';

    filterTextModules(modules).forEach((module) => {
        if (shouldContinue(text, limit)) {
            // check length to avoid module parsing
            module.columns.forEach((column) => {
                if (shouldContinue(text, limit)) {
                    // check length to avoid column parsing
                    const content = column.content;

                    if (content) {
                        try {
                            const rootNode = JSON.parse(content);

                            if (isRootNode(rootNode)) {
                                text = collectTextFromNode(
                                    rootNode.root,
                                    limit,
                                    text
                                );
                            }
                        } catch {}
                    }
                }
            });
        }
    });

    // replace all repeating whitespaces + trim
    const fixedText = text.replaceAll(/(\s{2,})/g, ' ').trim();

    return limit > 0 ? fixedText.substring(0, limit) : fixedText;
};
